import { useEffect, useRef, type ReactNode } from "react";
import { animate, motion, useMotionValue, useTransform, type PanInfo } from "framer-motion";
import { Trash2 } from "lucide-react";

// iOS-style swipe-to-remove row (the Mail / Messages gesture): drag the row left to
// reveal a red Remove behind it; a long or fast swipe removes it outright. Only one
// row is open at a time — the parent owns `open`. A tap on an open row closes it
// instead of passing through, so a half-swipe never opens the vendor by accident.

const REVEAL = 88; // px the Remove button needs
const FULL_SWIPE = 0.55; // share of the row's width that removes without a tap

interface Props {
  children: ReactNode;
  label: string; // what Remove removes, for screen readers
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemove: () => void;
  onTap: () => void;
  testId?: string;
}

export function SwipeToRemove({ children, label, open, onOpenChange, onRemove, onTap, testId }: Props) {
  const rowRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const dragged = useRef(false);
  // The red ground fades in as the row moves, so a resting row shows nothing behind it.
  const actionOpacity = useTransform(x, [-REVEAL, -16, 0], [1, 0.4, 0]);

  const spring = { type: "spring" as const, stiffness: 500, damping: 42 };
  const settle = (to: number) => animate(x, to, spring);

  // Keep the row where `open` says (another row opening closes this one).
  useEffect(() => {
    if (!dragged.current) settle(open ? -REVEAL : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const remove = () => {
    const width = rowRef.current?.offsetWidth ?? 400;
    animate(x, -width, { duration: 0.18, ease: "easeIn" }).then(onRemove);
  };

  const onDragEnd = (_: unknown, info: PanInfo) => {
    const width = rowRef.current?.offsetWidth ?? 400;
    const offset = x.get();
    if (offset < -width * FULL_SWIPE || (info.velocity.x < -900 && offset < -REVEAL / 2)) {
      remove();
    } else if (offset < -REVEAL / 2 || info.velocity.x < -300) {
      settle(-REVEAL);
      onOpenChange(true);
    } else {
      settle(0);
      onOpenChange(false);
    }
    // The click that ends a drag must not open the vendor.
    setTimeout(() => (dragged.current = false), 0);
  };

  return (
    // Inside an <AnimatePresence>: a removed row folds away and the rows below slide up.
    <motion.div
      ref={rowRef}
      layout="position"
      exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: "easeOut" } }}
      className="relative overflow-hidden rounded-lg"
      data-testid={testId}
    >
      <motion.button
        type="button"
        aria-label={`Remove ${label}`}
        tabIndex={open ? 0 : -1}
        onClick={remove}
        style={{ opacity: actionOpacity }}
        className="absolute inset-y-0 right-0 w-full flex items-center justify-end bg-red-500 text-white rounded-lg"
        data-testid={testId ? `${testId}-remove` : undefined}
      >
        <span className="flex flex-col items-center justify-center gap-1 text-xs font-medium" style={{ width: REVEAL }}>
          <Trash2 className="h-5 w-5" />
          Remove
        </span>
      </motion.button>
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -10000, right: 0 }}
        dragElastic={{ left: 0.15, right: 0 }}
        dragMomentum={false}
        style={{ x, touchAction: "pan-y" }}
        onDragStart={() => (dragged.current = true)}
        onDragEnd={onDragEnd}
        onClick={() => {
          if (dragged.current) return;
          if (open) onOpenChange(false);
          else onTap();
        }}
        className="relative"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
